import { Params } from 'app/types';
import { ReactNode, useRef } from 'react';
import { Controller } from 'react-hook-form';

import {
  Box,
  CircularProgress,
  FormControl,
  FormHelperText,
  Input,
  ListSubheader,
  MenuItem,
  Select,
  SelectChangeEvent,
} from '@mui/material';

import LabelTooltip from '../label-tooltip/LabelTooltip';

export interface BasicSelectProps {
  collection: Params[];
  label: string;
  control: any;
  inputName: string;
  required?: boolean;
  disabled?: boolean;
  helperText?: string;
  onChange?: (event: SelectChangeEvent<any>) => void;
  isOptional?: boolean;
  tooltipText?: string | ReactNode;
  isLoading?: boolean;
}

export default function BasicSelect(props: BasicSelectProps) {
  const {
    collection,
    control,
    label,
    inputName,
    required,
    disabled,
    onChange,
    helperText,
    isOptional,
    tooltipText,
    isLoading = false,
  } = props;

  // Trigger whole form onchange
  const myRef = useRef<HTMLInputElement>();
  const isWithGroups = collection.some((item) => item.id === 'optgroup');

  return (
    <Controller
      name={inputName}
      control={control}
      render={({ field, fieldState }) => (
        <>
          <LabelTooltip
            tooltipText={tooltipText}
            label={label}
            disabled={disabled}
            inputName={inputName}
            required={required}
            error={!!fieldState.error}
          />
          <FormControl error={!!fieldState.error}>
            <Select
              {...field}
              disabled={disabled}
              displayEmpty
              error={!!fieldState.error}
              input={
                <Input
                  ref={myRef}
                  endAdornment={
                    isLoading ? (
                      <Box paddingRight="35px">
                        <CircularProgress color="inherit" size={20} />
                      </Box>
                    ) : null
                  }
                />
              }
              onChange={(e) => {
                field.onChange(e);
                onChange && onChange(e);

                if (myRef.current !== null) {
                  myRef.current?.dispatchEvent(new Event('change', { bubbles: true }));
                }
              }}
            >
              <MenuItem disabled={!isOptional} value="" className="empty">
                - Select -
              </MenuItem>
              {collection.map((item: Params, index: number) => {
                if (item.id === 'optgroup') {
                  return (
                    <ListSubheader sx={{ pt: 2, lineHeight: '1.38' }} key={index}>
                      {item.name}
                    </ListSubheader>
                  );
                } else {
                  return (
                    <MenuItem key={`${index}-${item.id}`} value={item.id} sx={isWithGroups ? { ml: 2 } : {}}>
                      <Box color={`${item.color}`}>{item.name}</Box>
                    </MenuItem>
                  );
                }
              })}
            </Select>
            {(!!fieldState.error || helperText) && (
              <FormHelperText>{fieldState.error?.message || helperText}</FormHelperText>
            )}
          </FormControl>
        </>
      )}
    />
  );
}
